import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Heart, CheckCircle, Users, Share2, Bookmark, BookmarkCheck } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NeedPostCard = ({ post, getCategoryName }) => {
  const { t, language } = useLanguage();
  const { isAuthenticated, user, token } = useAuth();
  const [isFavorited, setIsFavorited] = useState(user?.favorites?.includes(post.post_id));
  const progress = Math.min((post.amount_raised / post.amount_needed) * 100, 100);
  const timeAgo = getTimeAgo(post.created_at, language);

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const shareUrl = `${API_URL}/api/share/post/${post.post_id}`;
    const sharePrefix = "Buongiorno cari amici non scorrete subito via ma guardate: ";
    if (navigator.share) {
      try { await navigator.share({ title: post.title, text: `${sharePrefix}${post.title}`, url: shareUrl }); } catch {}
    } else {
      navigator.clipboard.writeText(`${sharePrefix}${shareUrl}`);
      toast.success(language === 'it' ? 'Link copiato!' : 'Link copied!');
    }
  };

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) { toast.error(language === 'it' ? 'Devi accedere per salvare' : 'Login to save'); return; }
    try {
      if (isFavorited) {
        await axios.delete(`${API_URL}/api/posts/${post.post_id}/favorite`, { headers: { Authorization: `Bearer ${token}` } });
        setIsFavorited(false);
      } else {
        await axios.post(`${API_URL}/api/posts/${post.post_id}/favorite`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setIsFavorited(true);
      }
    } catch {}
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden card-lift group relative"
      data-testid={`post-card-${post.post_id}`}>
      {/* Highlighted badge */}
      {post.is_highlighted && (
        <div className="absolute top-3 left-3 z-10">
          <Badge className="bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-md animate-pulse-glow">
            {language === 'it' ? 'In evidenza' : 'Featured'}
          </Badge>
        </div>
      )}

      {/* Action buttons overlay */}
      <div className="absolute top-3 right-3 z-10 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200">
        <button data-testid={`share-card-btn-${post.post_id}`} onClick={handleShare}
          className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white hover:scale-110 transition-all">
          <Share2 className="h-3.5 w-3.5 text-stone-600" />
        </button>
        <button data-testid={`favorite-card-btn-${post.post_id}`} onClick={handleFavorite}
          className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white hover:scale-110 transition-all">
          {isFavorited
            ? <BookmarkCheck className="h-3.5 w-3.5 text-emerald-600" />
            : <Bookmark className="h-3.5 w-3.5 text-stone-600" />}
        </button>
      </div>

      <Link to={`/posts/${post.post_id}`}>
        {/* Image with gradient overlay */}
        {post.images && post.images.length > 0 ? (
          <div className="aspect-[4/3] overflow-hidden relative">
            <img src={post.images[0]} alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            {/* Progress overlay */}
            <div className="absolute bottom-3 left-3 right-3">
              <div className="flex items-center justify-between text-white text-xs font-medium mb-1">
                <span className="bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded-full">
                  {progress.toFixed(0)}%
                </span>
                <span className="bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded-full">
                  {post.amount_raised.toFixed(0)} / {post.amount_needed.toFixed(0)} {post.currency}
                </span>
              </div>
              <div className="h-1.5 bg-white/30 rounded-full overflow-hidden backdrop-blur-sm">
                <div className="h-full bg-amber-400 rounded-full transition-all duration-1000"
                  style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        ) : (
          <div className="aspect-[4/3] bg-gradient-to-br from-emerald-100 via-emerald-50 to-amber-50 flex items-center justify-center relative">
            <Heart className="h-14 w-14 text-emerald-300/60" />
            <div className="absolute bottom-3 left-3 right-3">
              <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-1000"
                  style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-4">
          {/* Tags row */}
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <Badge variant="secondary" className="bg-stone-100 text-stone-600 text-[11px]">
              {getCategoryName(post.category)}
            </Badge>
            {post.is_verified && (
              <Badge className="bg-blue-50 text-blue-600 text-[11px] border border-blue-100">
                <CheckCircle className="h-3 w-3 mr-0.5" />{t('verified')}
              </Badge>
            )}
            <span className="text-[11px] text-stone-400 ml-auto">{timeAgo}</span>
          </div>

          {/* Title */}
          <h3 className="font-heading text-base font-semibold text-stone-900 mb-1.5 line-clamp-2 group-hover:text-emerald-800 transition-colors">
            {post.title}
          </h3>

          {/* Description */}
          <p className="text-stone-500 text-sm line-clamp-2 mb-3 leading-relaxed">
            {post.description}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-stone-100">
            <div className="flex items-center gap-2">
              <Avatar className="h-7 w-7 ring-2 ring-stone-100">
                <AvatarImage src={post.user_picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${post.user_id}`} />
                <AvatarFallback className="text-xs bg-emerald-100 text-emerald-700">{post.user_name[0]}</AvatarFallback>
              </Avatar>
              <span className="text-sm text-stone-600 truncate max-w-[100px]">{post.user_name}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-stone-400 text-xs">
                <Users className="h-3.5 w-3.5" />
                <span>{post.donors_count}</span>
              </div>
              <div className="flex items-center gap-1 text-amber-500 text-xs font-semibold">
                <Heart className="h-3.5 w-3.5" fill="currentColor" />
                <span>{post.amount_raised.toFixed(0)}{post.currency === 'EUR' ? '€' : post.currency}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

function getTimeAgo(dateStr, lang) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return lang === 'it' ? 'Ora' : 'Now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return date.toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', { day: 'numeric', month: 'short' });
}

export default NeedPostCard;
